import React, { useState, useCallback, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  useTheme,
  alpha,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  ArrowForward as NextIcon, 
  CheckCircle as SuccessIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import ClientFileUpload from '../../components/ClientFileUpload';
import { ClientFile } from '../../services/clientFilesService';
import { useKeywordReadiness } from '../../hooks/useKeywordReadiness';
import { workflowService } from '../../services/api/workflowService';

const FileUploadStep: React.FC = () => {
  const { clientId, workflowId } = useParams<{ clientId: string; workflowId: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  
  const [files, setFiles] = useState<ClientFile[]>([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  // TODO: Fetch client name from API
  const clientName = `Client ${clientId}`;
  
  // Use keyword readiness hook to get keyword count
  const { data: readinessData, startPolling, stopPolling } = useKeywordReadiness({
    clientId,
    workflowId,
    enabled: true,
    pollingInterval: 3000
  });
  
  // Handle file status changes from ClientFileUpload component
  const handleStatusChange = useCallback((updatedFiles: ClientFile[]) => {
    setFiles(updatedFiles);
    
    // Mark initial load as complete after first status update
    if (isInitialLoad) {
      setIsInitialLoad(false);
    }
  }, [isInitialLoad]);
  
  // Check if all files are loaded
  const allFilesLoaded = files.length > 0 && files.every(file => 
    file.load_status === 'LOAD COMPLETED'
  );
  
  // Check if there are any files being processed
  const hasProcessingFiles = files.some(file => 
    file.load_status === 'Pending' || file.load_status === 'Processing' || file.load_status === 'LOAD TRIGGERED' || file.load_status === 'triggered'
  );
  
  // Check if queries are actually ready (not just files uploaded)
  const queriesReady = allFilesLoaded && 
    readinessData.keywordCount !== undefined && 
    readinessData.keywordCount > 0;
  
  // Check if files are loaded but queries aren't ready yet
  const waitingForQueries = allFilesLoaded && !queriesReady;
  
  // Check if there are no files or files are not ready
  const needsUpload = files.length === 0 || !allFilesLoaded;
  
  // Update module status to 'completed' when queries are actually ready
  useEffect(() => {
    const completeFileUpload = async () => {
      if (queriesReady && workflowId) {
        try {
          const dbWorkflow = await workflowService.getDbWorkflow(workflowId);
          await workflowService.completeFileUpload(dbWorkflow.workflow_id);
          console.log('File upload marked as completed');
        } catch (error) {
          console.error('Error marking file upload as completed:', error);
        }
      }
    };
    
    completeFileUpload();
  }, [queriesReady, workflowId]);
  
  // Start polling for keyword count when all files are loaded, stop when queries are ready
  useEffect(() => {
    if (allFilesLoaded && !queriesReady && clientId && workflowId) {
      // Files are uploaded but queries aren't ready yet - keep polling
      startPolling();
    } else if (queriesReady) {
      // Queries are ready - stop polling
      stopPolling();
    } else {
      // Files aren't uploaded yet - don't poll
      stopPolling();
    }
    
    return () => {
      stopPolling();
    };
  }, [allFilesLoaded, queriesReady, clientId, workflowId, startPolling, stopPolling]);
  
  const handleProceedToNext = () => {
    navigate(`/workflow2/${clientId}/${workflowId}/keyword`);
  };
  
  const handleManualRefresh = () => {
    // Force refresh the keyword count with animation
    if (clientId && workflowId && !isRefreshing) {
      setIsRefreshing(true);
      startPolling();
      
      // Reset animation after 1 second
      setTimeout(() => {
        setIsRefreshing(false);
      }, 1000);
    }
  };
  
  return (
    <Box>
      <Typography variant="body1" color="text.secondary" paragraph>
      Upload CSV data at the search query level. Note that you must include Query and select metrics from each source.  
      AI will support you if you've excluded metrics.
      </Typography>
      
      {clientId && workflowId && (
        <ClientFileUpload
          clientId={clientId}
          clientName={clientName}
          workflowId={workflowId}
          autoRefresh={true}
          refreshInterval={5000}
          onStatusChange={handleStatusChange}
        />
      )}
      
      {/* Show information panel when files are needed or being processed (after initial load) */}
      {!isInitialLoad && needsUpload && !allFilesLoaded && (
        <Paper
          elevation={1}
          sx={{
            p: 3,
            mt: 3,
            backgroundColor: alpha(theme.palette.info.main, 0.08),
            border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
          }}
        >
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: theme.palette.info.main }}>
            {files.length === 0 ? '📤 Upload Files to Get Started' : '⏳ Processing Files'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {files.length === 0 
              ? 'Please upload at least one CSV file using the upload area above. Once uploaded, files will be automatically processed and made ready for query analysis.'
              : hasProcessingFiles
                ? 'Your files are being processed. Please wait for all files to complete loading before proceeding to the next step.'
                : 'Please ensure all files have been successfully loaded before proceeding.'
            }
          </Typography>
        </Paper>
      )}
      
      {/* Show simple waiting state when files are uploaded but queries aren't ready yet */}
      {!isInitialLoad && waitingForQueries && (
        <Paper
          elevation={2}
          sx={{
            p: 3,
            mt: 3,
            backgroundColor: alpha(theme.palette.info.main, 0.08),
            border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
            textAlign: 'center'
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={24} color="primary" />
              <Typography variant="body1" color="text.secondary">
                Processing uploaded files and preparing queries...
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              This may take a few minutes. Please wait.
            </Typography>
          </Box>
        </Paper>
      )}
      
      {/* Show success and next step button at the bottom when queries are ready (after initial load) */}
      {!isInitialLoad && queriesReady && (
        <Paper
          elevation={3}
          sx={{
            p: 3,
            mt: 3,
            backgroundColor: alpha(theme.palette.success.main, 0.08),
            border: `2px solid ${theme.palette.success.main}`,
            textAlign: 'center'
          }}
        >
          <SuccessIcon 
            sx={{ 
              fontSize: 48, 
              color: theme.palette.success.main,
              mb: 2
            }} 
          />
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
            All Files Loaded Successfully!
          </Typography>
          
          {/* Display keyword count */}
          {readinessData.keywordCount !== undefined ? (
            <Box sx={{ mb: 2 }}>
              <Chip 
                label={`${readinessData.keywordCount.toLocaleString()} queries available`}
                color="success"
                size="medium"
                onClick={handleManualRefresh}
                onDelete={handleManualRefresh}
                deleteIcon={
                  <Tooltip title="Refresh query count">
                    <RefreshIcon 
                      sx={{
                        cursor: 'pointer',
                        animation: isRefreshing ? 'spin 1s linear' : 'none',
                        '@keyframes spin': {
                          '0%': {
                            transform: 'rotate(0deg)',
                          },
                          '100%': {
                            transform: 'rotate(360deg)',
                          },
                        },
                      }}
                    />
                  </Tooltip>
                }
                sx={{ 
                  fontSize: '1rem',
                  fontWeight: 600,
                  py: 2,
                  px: 1,
                  cursor: 'pointer',
                  '& .MuiChip-deleteIcon': {
                    color: theme.palette.success.contrastText,
                    '&:hover': {
                      color: theme.palette.success.contrastText,
                      opacity: 0.8
                    }
                  }
                }}
              />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 2 }}>
              <CircularProgress size={16} color="success" />
              <Typography variant="body2" color="text.secondary">
                Counting queries...
              </Typography>
            </Box>
          )}
          
          <Typography variant="body2" color="text.secondary" paragraph>
            Your files have been processed and are ready for query analysis.
            If the query count seems incomplete, click the refresh icon above to update it.
          </Typography>
          <Button
            variant="contained"
            size="large"
            color="success"
            onClick={handleProceedToNext}
            endIcon={<NextIcon />}
            sx={{ 
              mt: 2,
              fontWeight: 600,
              boxShadow: theme.shadows[4],
              '&:hover': {
                boxShadow: theme.shadows[8],
              }
            }}
          >
            Proceed to Query Analysis
          </Button>
        </Paper>
      )}
    </Box>
  );
};

export default FileUploadStep;

